import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Design system §6. Height 38, radius 10, 1px border, 12px side padding, 14px text.
 * Numeric fields (size, line height, zoom) should pass `className="font-mono text-[13.5px]"`.
 * Error state is driven by `aria-invalid`.
 *
 * `forwardRef` is load-bearing on React 18, exactly as it is on `Button`: a plain function
 * component silently drops the ref, so anything that has to focus or select the field —
 * an inline rename, a form library, a validation error jumping to it — gets nothing back.
 * Revisit only on React 19, where `ref` is a plain prop.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(function Input(
  { className, type, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(
        'h-[38px] w-full min-w-0 rounded-lg border border-input bg-card px-3 text-sm text-foreground transition-[color,border-color,box-shadow] duration-(--animate-duration-hover) ease-(--ease-standard) outline-none',
        'selection:bg-accent selection:text-accent-foreground placeholder:text-disabled',
        'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-semibold file:text-foreground',
        'focus-visible:ring-focus',
        'disabled:cursor-not-allowed disabled:border-divider disabled:bg-surface-2 disabled:text-disabled',
        'aria-invalid:border-destructive aria-invalid:focus-visible:ring-focus-destructive',
        className,
      )}
      {...props}
    />
  );
});

export { Input };
